/**
 * HTML sanitiser — the single approved wrapper for ANY content that
 * reaches the DOM via React's `dangerouslySetInnerHTML`.
 *
 * Why this exists: inbound email HTML, AI-generated guide content, and
 * QA-agent diagnostic output were rendered raw, exposing stored XSS on
 * the authenticated app origin. DOMPurify strips `<script>`, event
 * handlers, `javascript:` URLs, and other XSS vectors while preserving
 * normal formatting.
 *
 * Always use `sanitizeHtml(content)` instead of passing content
 * straight to `dangerouslySetInnerHTML`. A grep for
 * `dangerouslySetInnerHTML` should never find an unsanitised source.
 */

import DOMPurify from "dompurify";

// Defaults: strip <script>, event handlers, javascript: URLs, data: in
// most contexts. DOMPurify's default allow-list is intentionally
// conservative — appropriate for inbound third-party HTML like email
// bodies and AI output. Targets are sanitised once at render time;
// idempotent for already-clean content.
export function sanitizeHtml(dirty: string | null | undefined): string {
  if (!dirty) return "";
  return DOMPurify.sanitize(dirty, {
    // Keep formatting + tables + images + links (email/document
    // content commonly contains all of these), drop scripts/iframes/
    // forms/objects and any on* attribute.
    USE_PROFILES: { html: true },
    // No SVG (XSS vector via <svg><script>) and no MathML — neither
    // is needed for the content we render.
    ADD_TAGS: [],
    ADD_ATTR: [],
    // Force-strip dangerous URL schemes from href/src.
    ALLOWED_URI_REGEXP:
      /^(?:(?:https?|mailto|tel|cid):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  });
}
